import React, { useState, useEffect, useCallback } from 'react';
import API_URL from './config';
import './PublicCalendar.css';

// Convert 24-hour format to 12-hour format
function formatTime12h(time24) {
  if (!time24) return '';
  // Handle format like "9:00 AM" (already 12h)
  if (time24.includes('AM') || time24.includes('PM')) {
    return time24;
  }
  // Handle range format like "09:00 - 09:30"
  if (time24.includes(' - ')) {
    const [start, end] = time24.split(' - ');
    return `${formatTime12h(start)} - ${formatTime12h(end)}`;
  }
  const [hours, minutes] = time24.split(':').map(Number);
  const period = hours >= 12 ? 'PM' : 'AM';
  const hours12 = hours % 12 || 12;
  return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
}

function AdminBookings({ token, refreshKey, onBookingChange }) {
  const [bookings, setBookings] = useState([]);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [timezone, setTimezone] = useState('America/New_York');
  const [filter, setFilter] = useState('pending');
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [sortField, setSortField] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');
  const [selectedIds, setSelectedIds] = useState([]);
  const [notesModal, setNotesModal] = useState({ open: false, id: null, action: null, notes: '', sendNotification: true });
  const [bulkNotesModal, setBulkNotesModal] = useState({ open: false, action: null, notes: '', sendNotification: true });
  const [deleteModal, setDeleteModal] = useState({ open: false, id: null, reason: '', sendNotification: true });
  const [expandedUserInfo, setExpandedUserInfo] = useState(new Set());
  
  // Calendar state
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [calendarSelectedDay, setCalendarSelectedDay] = useState(null);
  const [showCalendar, setShowCalendar] = useState(true);

  const toggleUserInfo = (bookingId) => {
    setExpandedUserInfo(prev => {
      const newSet = new Set(prev);
      if (newSet.has(bookingId)) {
        newSet.delete(bookingId);
      } else {
        newSet.add(bookingId);
      }
      return newSet;
    });
  };

  const fetchBookings = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/bookings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setBookings(Array.isArray(data) ? data : []);
    } catch {
      setBookings([]);
    }
  }, [token]);

  useEffect(() => {
    fetchBookings();
    // Fetch timezone setting
    fetch(`${API_URL}/api/settings/timezone`)
      .then(r => r.json())
      .then(data => { if (data.value) setTimezone(data.value); })
      .catch(() => {});
  }, [fetchBookings, refreshKey]);

  // Single booking status update with notes (supports grouped bookings)
  async function handleUpdateStatus(id, status, admin_notes = '', booking_ids = null, sendNotification = false) {
    setMessage('');
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/admin/bookings/${id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status, admin_notes, booking_ids, sendNotification })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `Failed to ${status} booking`);
      setMessage(`Booking ${status} successfully.`);
      fetchBookings();
      if (onBookingChange) onBookingChange();
    } catch (err) {
      setError(err.message);
    }
  }

  // Delete single booking
  async function handleDelete(id, reason, sendNotification = false) {
    setMessage('');
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/admin/bookings/${id}`, {
        method: 'DELETE',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ reason, sendNotification })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to delete booking');
      setMessage('Booking deleted successfully.');
      setDeleteModal({ open: false, id: null, reason: '' });
      fetchBookings();
      if (onBookingChange) onBookingChange();
    } catch (err) {
      setError(err.message);
    }
  }
  
  // Open delete modal
  function openDeleteModal(id) {
    setDeleteModal({ open: true, id, reason: '' });
  }
  
  // Confirm delete modal
  function confirmDeleteModal() {
    if (!deleteModal.reason.trim()) {
      setError('A reason is required when deleting a booking');
      return;
    }
    handleDelete(deleteModal.id, deleteModal.reason, deleteModal.sendNotification);
  }

  // Bulk update
  async function handleBulkUpdate(status, admin_notes = '', sendNotification = false) {
    setMessage('');
    setError('');
    if (selectedIds.length === 0) {
      setError('No bookings selected');
      return;
    }
    const expandedIds = getExpandedSelectedIds();
    try {
      const res = await fetch(`${API_URL}/api/admin/bookings/bulk/update`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ ids: expandedIds, status, admin_notes, sendNotification })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to update bookings');
      setMessage(data.message);
      setSelectedIds([]);
      fetchBookings();
      if (onBookingChange) onBookingChange();
    } catch (err) {
      setError(err.message);
    }
  }

  // Bulk delete
  async function handleBulkDelete() {
    setMessage('');
    setError('');
    if (selectedIds.length === 0) {
      setError('No bookings selected');
      return;
    }
    const expandedIds = getExpandedSelectedIds();
    const totalBookings = bookings.length;
    const filteredTotal = filteredBookings.length;
    
    let confirmMsg = `Are you sure you want to permanently delete ${expandedIds.length} booking(s)?`;
    if (filter !== 'all' && filteredTotal < totalBookings) {
      confirmMsg += `\n\nNote: You are viewing "${filter}" bookings only. ${totalBookings - filteredTotal} other booking(s) will NOT be deleted.`;
    }
    
    if (!window.confirm(confirmMsg)) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/bookings/bulk/delete`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ ids: expandedIds })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to delete bookings');
      setMessage(data.message);
      setSelectedIds([]);
      fetchBookings();
      if (onBookingChange) onBookingChange();
    } catch (err) {
      setError(err.message);
    }
  }

  // Send manual reminder for a booking
  async function handleSendReminder(bookingId) {
    setMessage('');
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/admin/bookings/${bookingId}/send-reminder`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to send reminder');
      setMessage(data.message || 'Reminder sent successfully');
      fetchBookings(); // Refresh to show updated reminder_sent status
    } catch (err) {
      setError(err.message);
    }
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const datePart = dateStr.slice(0, 10);
    const [year, month, day] = datePart.split('-').map(Number);
    const d = new Date(year, month - 1, day);
    return d.toLocaleDateString('en-US', { timeZone: timezone });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    return d.toLocaleString('en-US', { timeZone: timezone });
  };

  const getStatusBadge = (status) => {
    const colors = {
      pending: '#3b82f6',
      approved: '#fbbf24',
      rejected: '#ef4444'
    };
    return (
      <span style={{
        background: colors[status] || '#9ca3af',
        color: '#fff',
        padding: '2px 8px',
        borderRadius: '4px',
        fontSize: '0.85rem'
      }}>
        {status}
      </span>
    );
  };

  // Filter, search, and sort logic
  const filteredBookings = bookings
    .filter(b => {
      if (filter !== 'all' && b.status !== filter) return false;
      if (dateFrom && b.booking_date < dateFrom) return false;
      if (dateTo && b.booking_date > dateTo) return false;
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        const fullName = `${b.first_name || ''} ${b.last_name || ''}`.trim();
        const searchFields = [
          fullName, b.username, b.email, b.phone, b.organization,
          b.booking_type, b.time_slot
        ].filter(Boolean).map(f => f.toLowerCase());
        if (!searchFields.some(f => f.includes(term))) return false;
      }
      return true;
    })
    .sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];
      if (sortField === 'booking_date' || sortField === 'created_at') {
        aVal = new Date(aVal);
        bVal = new Date(bVal);
      }
      if (typeof aVal === 'string') aVal = aVal.toLowerCase();
      if (typeof bVal === 'string') bVal = bVal.toLowerCase();
      
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

  const pendingCount = bookings.filter(b => b.status === 'pending').length;

  // Get approved bookings map for checking slot availability
  const getApprovedBookingsMap = () => {
    const approvedBookings = bookings.filter(b => b.status === 'approved');
    const slotMap = {};
    approvedBookings.forEach(b => {
      const key = `${b.booking_date}_${b.time_slot}`;
      if (!slotMap[key]) slotMap[key] = { full_turf: 0, half_turf: 0, cage_rental: 0 };
      slotMap[key][b.booking_type]++;
    });
    return slotMap;
  };
  const approvedSlotsMap = getApprovedBookingsMap();

  // Check if a specific booking type can still be approved for a slot
  const canApproveBookingType = (booking) => {
    const key = `${booking.booking_date}_${booking.time_slot}`;
    const slot = approvedSlotsMap[key] || { full_turf: 0, half_turf: 0, cage_rental: 0 };
    
    // If any full_turf is booked, nothing else can be approved
    if (slot.full_turf > 0) return false;
    
    // Check based on what type is being requested
    switch (booking.booking_type) {
      case 'full_turf':
        // Can only approve if slot is completely empty
        return slot.half_turf === 0 && slot.cage_rental === 0;
      case 'half_turf':
        // Max 2 if no cages, max 1 if cages exist
        if (slot.cage_rental > 0) {
          return slot.half_turf < 1;
        }
        return slot.half_turf < 2;
      case 'cage_rental':
        // Max 3 cages, only if half turf <= 1
        return slot.half_turf <= 1 && slot.cage_rental < 3;
      default:
        return false;
    }
  };

  // Get pending bookings map by slot and type
  const getPendingBookingsMap = () => {
    const pendingBookings = bookings.filter(b => b.status === 'pending');
    const slotMap = {};
    pendingBookings.forEach(b => {
      const key = `${b.booking_date}_${b.time_slot}`;
      if (!slotMap[key]) slotMap[key] = { full_turf: 0, half_turf: 0, cage_rental: 0 };
      slotMap[key][b.booking_type]++;
    });
    return slotMap;
  };
  const pendingSlotsMap = getPendingBookingsMap();

  // Check if a specific booking exceeds capacity when combined with approved + pending of same type
  const isBookingOverCapacity = (booking) => {
    const key = `${booking.booking_date}_${booking.time_slot}`;
    const approved = approvedSlotsMap[key] || { full_turf: 0, half_turf: 0, cage_rental: 0 };
    const pending = pendingSlotsMap[key] || { full_turf: 0, half_turf: 0, cage_rental: 0 };
    
    switch (booking.booking_type) {
      case 'full_turf':
        // Max 1 full turf, and slot must be empty
        // Conflict if: more than 1 pending full turf, or any approved bookings exist
        return pending.full_turf > 1 || approved.full_turf > 0 || approved.half_turf > 0 || approved.cage_rental > 0;
      
      case 'half_turf':
        // Max 2 if no cages (approved or pending), max 1 if cages exist
        const totalCages = approved.cage_rental + pending.cage_rental;
        const totalPendingHalf = pending.half_turf;
        if (totalCages > 0) {
          // With cages, max 1 half turf
          return (approved.half_turf + totalPendingHalf) > 1;
        } else {
          // Without cages, max 2 half turf
          return (approved.half_turf + totalPendingHalf) > 2;
        }
      
      case 'cage_rental':
        // Max 3 cages, only allowed if half turf <= 1
        const totalHalf = approved.half_turf + pending.half_turf;
        // If there will be 2 half turfs (no cages allowed), it's a conflict
        if (totalHalf > 1) return true;
        // Check if pending cages exceed available slots
        const availableCageSlots = 3 - approved.cage_rental;
        return pending.cage_rental > availableCageSlots;
      
      default:
        return false;
    }
  };

  // Check if a booking is in conflict (pending requests of same type exceed capacity)
  const isConflicting = (booking) => {
    if (booking.status !== 'pending') return false;
    // Conflict with approved booking (can't be approved)
    if (!canApproveBookingType(booking)) return true;
    // Pending requests of this type exceed available capacity
    if (isBookingOverCapacity(booking)) return true;
    return false;
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return '↕';
    return sortDirection === 'asc' ? '↑' : '↓';
  };

  // Get all individual booking IDs from a grouped booking
  const getAllBookingIds = (booking) => {
    return booking.booking_ids && booking.booking_ids.length > 0 
      ? booking.booking_ids 
      : [booking.id];
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === filteredBookings.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredBookings.map(b => b.id));
    }
  };

  const toggleSelect = (id) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter(i => i !== id));
    } else {
      setSelectedIds([...selectedIds, id]);
    }
  };

  // Get all individual booking IDs for bulk operations (expands grouped bookings)
  const getExpandedSelectedIds = () => {
    const expanded = [];
    for (const id of selectedIds) {
      const booking = bookings.find(b => b.id === id);
      if (booking) {
        expanded.push(...getAllBookingIds(booking));
      } else {
        expanded.push(id);
      }
    }
    return [...new Set(expanded)]; // Remove duplicates
  };

  const openNotesModal = (id, action) => {
    setNotesModal({ open: true, id, action, notes: '', sendNotification: action === 'rejected' });
  };

  const confirmNotesModal = () => {
    // Find the booking to get all booking_ids for grouped bookings
    const booking = bookings.find(b => b.id === notesModal.id);
    const booking_ids = booking ? getAllBookingIds(booking) : null;
    handleUpdateStatus(notesModal.id, notesModal.action, notesModal.notes, booking_ids, notesModal.sendNotification);
    setNotesModal({ open: false, id: null, action: null, notes: '', sendNotification: true });
  };

  const openBulkNotesModal = (action) => {
    if (selectedIds.length === 0) {
      setError('No bookings selected');
      return;
    }
    setBulkNotesModal({ open: true, action, notes: '', sendNotification: action === 'rejected' });
  };

  const confirmBulkNotesModal = () => {
    handleBulkUpdate(bulkNotesModal.action, bulkNotesModal.notes, bulkNotesModal.sendNotification);
    setBulkNotesModal({ open: false, action: null, notes: '', sendNotification: true });
  };

  // ============ CALENDAR FUNCTIONS ============
  
  // Calendar navigation
  const goToCalendarToday = () => setCalendarDate(new Date());
  
  const goToCalendarPrev = () => {
    const newDate = new Date(calendarDate);
    newDate.setMonth(newDate.getMonth() - 1);
    setCalendarDate(newDate);
  };
  
  const goToCalendarNext = () => {
    const newDate = new Date(calendarDate);
    newDate.setMonth(newDate.getMonth() + 1);
    setCalendarDate(newDate);
  };
  
  // Get calendar days for month view
  const getCalendarMonthDays = () => {
    const year = calendarDate.getFullYear();
    const month = calendarDate.getMonth();
    
    const firstDay = new Date(year, month, 1);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());
    
    const days = [];
    const current = new Date(startDate);
    
    for (let i = 0; i < 42; i++) {
      days.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }
    
    return days;
  };
  
  // Get bookings for a specific date (calendar view)
  const getCalendarBookingsForDate = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return bookings.filter(b => {
      const bookingDateStr = b.booking_date.split('T')[0];
      return bookingDateStr === dateStr;
    });
  };
  
  // Format time for calendar display
  const formatCalendarTime = (timeStr) => {
    if (!timeStr) return '';
    const [hours, minutes] = timeStr.split(':').map(Number);
    const period = hours >= 12 ? 'pm' : 'am';
    const displayHours = hours > 12 ? hours - 12 : hours === 0 ? 12 : hours;
    return `${displayHours}${period}`;
  };
  
  // Get color based on booking status
  const getStatusColor = (status) => {
    const colors = {
      'pending': '#3b82f6',    // blue
      'approved': '#22c55e',   // green
      'rejected': '#ef4444'    // red
    };
    return colors[status] || '#5f6368';
  };
  
  // Get color based on booking type
  const getTypeColor = (type) => {
    const colors = {
      'full_turf': '#1a73e8',
      'half_turf': '#34a853',
      'cage_rental': '#ea4335'
    };
    return colors[type] || '#5f6368';
  };
  
  // Format month/year for calendar header
  const formatCalendarMonthYear = () => {
    return calendarDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };
  
  // Format full date for modal header
  const formatCalendarFullDate = (date) => {
    return date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
  };
  
  // Handle clicking on a calendar day to filter
  const handleCalendarDayClick = (day, dayBookings) => {
    if (dayBookings.length > 0) {
      setCalendarSelectedDay({ date: day, bookings: dayBookings });
    }
  };
  
  // Close calendar modal
  const closeCalendarModal = () => {
    setCalendarSelectedDay(null);
  };
  
  // Handle clicking a booking in the calendar to filter table
  const handleCalendarBookingClick = (booking) => {
    // Set date filter to that booking's date
    const bookingDateStr = booking.booking_date.split('T')[0];
    setDateFrom(bookingDateStr);
    setDateTo(bookingDateStr);
    setFilter('all');
    closeCalendarModal();
  };
  
  const calendarWeekDays = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
  const calendarMonthDays = getCalendarMonthDays();
  const calendarToday = new Date();
  calendarToday.setHours(0, 0, 0, 0);

  // Get display name for booking type
  const getTypeDisplayName = (type) => {
    const names = {
      'full_turf': 'Full Turf',
      'half_turf': 'Half Turf',
      'cage_rental': 'Cage'
    };
    return names[type] || type;
  };

  // Get timezone display name
  const getTimezoneDisplay = (tz) => {
    const tzMap = {
      'America/New_York': 'Eastern Time (ET)',
      'America/Chicago': 'Central Time (CT)',
      'America/Denver': 'Mountain Time (MT)',
      'America/Los_Angeles': 'Pacific Time (PT)',
      'America/Anchorage': 'Alaska Time (AKT)',
      'Pacific/Honolulu': 'Hawaii Time (HT)',
      'America/Phoenix': 'Arizona Time (MST)',
      'America/Indiana/Indianapolis': 'Indiana (ET)',
    };
    return tzMap[tz] || tz;
  };

  return (
    <div className="admin-bookings-container">
      <h2>
        Manage Requests
        {pendingCount > 0 && (
          <span className="pending-badge">{pendingCount} pending</span>
        )}
      </h2>

      {/* Calendar Section */}
      <div className="admin-calendar-section">
        <div className="admin-calendar-header">
          <h3>Booking Calendar</h3>
          <button 
            className="toggle-calendar-btn"
            onClick={() => setShowCalendar(!showCalendar)}
          >
            {showCalendar ? 'Hide Calendar' : 'Show Calendar'}
          </button>
        </div>
        
        {showCalendar && (
          <div className="public-calendar admin-calendar">
            {/* Calendar Header */}
            <div className="calendar-header">
              <div className="calendar-nav">
                <button className="calendar-today-btn" onClick={goToCalendarToday}>Today</button>
                <button className="calendar-nav-btn" onClick={goToCalendarPrev} aria-label="Previous">
                  &#8249;
                </button>
                <button className="calendar-nav-btn" onClick={goToCalendarNext} aria-label="Next">
                  &#8250;
                </button>
                <h2 className="calendar-title">{formatCalendarMonthYear()}</h2>
              </div>
            </div>

            {/* Calendar Grid */}
            <div className="calendar-grid" style={{ display: 'block', width: '100%' }}>
              {/* Week day headers */}
              <div className="calendar-weekdays" style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', width: '100%' }}>
                {calendarWeekDays.map(day => (
                  <div key={day} className="calendar-weekday">{day}</div>
                ))}
              </div>

              {/* Calendar days */}
              <div className="calendar-days" style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', width: '100%' }}>
                {calendarMonthDays.map((day, idx) => {
                  const isCurrentMonth = day.getMonth() === calendarDate.getMonth();
                  const isToday = day.toDateString() === calendarToday.toDateString();
                  const dayBookings = getCalendarBookingsForDate(day);
                  
                  return (
                    <div 
                      key={idx} 
                      className={`calendar-day ${!isCurrentMonth ? 'other-month' : ''} ${isToday ? 'today' : ''}`}
                      onClick={() => handleCalendarDayClick(day, dayBookings)}
                      style={{ cursor: dayBookings.length > 0 ? 'pointer' : 'default' }}
                    >
                      <div className={`day-number ${isToday ? 'today-number' : ''}`}>
                        {day.getDate()}
                      </div>
                      <div className="day-bookings">
                        {dayBookings.slice(0, 3).map((booking, bIdx) => (
                          <div 
                            key={bIdx} 
                            className="booking-pill"
                            style={{ backgroundColor: getStatusColor(booking.status) }}
                            title={`${formatCalendarTime(booking.time_slot)} ${getTypeDisplayName(booking.booking_type)} (${booking.status})`}
                          >
                            <span className="booking-time">{formatCalendarTime(booking.time_slot)}</span>
                            <span className="booking-resource">{getTypeDisplayName(booking.booking_type)}</span>
                          </div>
                        ))}
                        {dayBookings.length > 3 && (
                          <div className="more-bookings">
                            +{dayBookings.length - 3} more
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Legend */}
            <div className="calendar-legend">
              <div className="legend-item">
                <span className="legend-color" style={{ backgroundColor: '#3b82f6' }}></span>
                <span>Pending</span>
              </div>
              <div className="legend-item">
                <span className="legend-color" style={{ backgroundColor: '#22c55e' }}></span>
                <span>Approved</span>
              </div>
              <div className="legend-item">
                <span className="legend-color" style={{ backgroundColor: '#ef4444' }}></span>
                <span>Rejected</span>
              </div>
            </div>

            {/* Footer info */}
            <div className="calendar-footer-info">
              <span>{bookings.length} total bookings</span>
              <span className="calendar-timezone">{getTimezoneDisplay(timezone)}</span>
            </div>
          </div>
        )}
        
        {/* Calendar Day Modal */}
        {calendarSelectedDay && (
          <div className="day-modal-overlay" onClick={closeCalendarModal}>
            <div className="day-modal" onClick={(e) => e.stopPropagation()}>
              <div className="day-modal-header">
                <h3>{formatCalendarFullDate(calendarSelectedDay.date)}</h3>
                <button className="day-modal-close" onClick={closeCalendarModal}>&times;</button>
              </div>
              <div className="day-modal-content">
                {calendarSelectedDay.bookings
                  .sort((a, b) => a.time_slot.localeCompare(b.time_slot))
                  .map((booking, idx) => (
                    <div 
                      key={idx} 
                      className="modal-booking-item"
                      style={{ borderLeftColor: getStatusColor(booking.status), cursor: 'pointer' }}
                      onClick={() => handleCalendarBookingClick(booking)}
                    >
                      <div>
                        <span className="modal-booking-time">
                          {formatTime12h(booking.time_slot)}
                        </span>
                        <span style={{ marginLeft: '8px', fontSize: '0.8rem', color: '#666' }}>
                          {booking.first_name && booking.last_name 
                            ? `${booking.first_name} ${booking.last_name}` 
                            : booking.username}
                        </span>
                      </div>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <span 
                          className="modal-booking-resource"
                          style={{ backgroundColor: getTypeColor(booking.booking_type) }}
                        >
                          {getTypeDisplayName(booking.booking_type)}
                        </span>
                        <span style={{
                          fontSize: '0.7rem',
                          padding: '2px 6px',
                          borderRadius: '4px',
                          backgroundColor: getStatusColor(booking.status),
                          color: '#fff'
                        }}>
                          {booking.status}
                        </span>
                      </div>
                    </div>
                  ))}
              </div>
              <div style={{ padding: '0.75rem', borderTop: '1px solid #dadce0', fontSize: '0.8rem', color: '#666', textAlign: 'center' }}>
                Click a booking to filter the table below
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Search and Date Filters */}
      <div className="admin-filters">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search by name, email, phone, org..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="date-filters">
          <label>
            From:
            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
          </label>
          <label>
            To:
            <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} />
          </label>
          {(dateFrom || dateTo) && (
            <button className="clear-dates-btn" onClick={() => { setDateFrom(''); setDateTo(''); }}>
              Clear
            </button>
          )}
        </div>
      </div>
      
      {/* Status Filter Tabs */}
      <div className="filter-tabs">
        <div className="filter-tabs-left">
          <button className={`filter-tab ${filter === 'pending' ? 'active' : ''}`} onClick={() => setFilter('pending')}>
            Pending
          </button>
          <button className={`filter-tab ${filter === 'approved' ? 'active' : ''}`} onClick={() => setFilter('approved')}>
            Approved
          </button>
          <button className={`filter-tab ${filter === 'rejected' ? 'active' : ''}`} onClick={() => setFilter('rejected')}>
            Rejected
          </button>
          <button className={`filter-tab ${filter === 'all' ? 'active' : ''}`} onClick={() => setFilter('all')}>
            All
          </button>
        </div>
        {selectedIds.length > 0 && (
          <button 
            className="trash-btn" 
            onClick={handleBulkDelete}
            title={`Delete ${selectedIds.length} selected booking(s)`}
          >
            🗑️
          </button>
        )}
      </div>

      {/* Bulk Actions */}
      {selectedIds.length > 0 && (
        <div className="bulk-actions">
          <span>{selectedIds.length} selected{filter !== 'all' && ` (${filter} only)`}</span>
          <button className="bulk-approve-btn" onClick={() => openBulkNotesModal('approved')}>
            Approve Selected
          </button>
          <button className="bulk-reject-btn" onClick={() => openBulkNotesModal('rejected')}>
            Reject Selected
          </button>
          <button className="bulk-clear-btn" onClick={() => setSelectedIds([])}>
            Clear Selection
          </button>
        </div>
      )}

      {message && <div className="success">{message}</div>}
      {error && <div className="error">{error}</div>}
      
      {filteredBookings.length === 0 ? (
        <p className="no-bookings">No {filter !== 'all' ? filter : ''} bookings found.</p>
      ) : (
        <table className="admin-bookings-table">
          <thead>
            <tr>
              <th>
                <input
                  type="checkbox"
                  checked={selectedIds.length === filteredBookings.length && filteredBookings.length > 0}
                  onChange={toggleSelectAll}
                />
              </th>
              <th className="sortable" onClick={() => handleSort('name')}>
                User {getSortIcon('name')}
              </th>
              <th className="sortable" onClick={() => handleSort('booking_date')}>
                Time Slot {getSortIcon('booking_date')}
              </th>
              <th className="sortable" onClick={() => handleSort('booking_type')}>
                Type {getSortIcon('booking_type')}
              </th>
              <th className="sortable" onClick={() => handleSort('price')}>
                Price {getSortIcon('price')}
              </th>
              <th className="sortable" onClick={() => handleSort('created_at')}>
                Requested {getSortIcon('created_at')}
              </th>
              <th className="actions-header">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredBookings.map(b => (
              <tr key={b.id} className={`booking-row status-${b.status}${isConflicting(b) ? ' conflict' : ''}`}>
                <td>
                  <input
                    type="checkbox"
                    checked={selectedIds.includes(b.id)}
                    onChange={() => toggleSelect(b.id)}
                  />
                </td>
                <td>
                  <div className="user-info">
                    <div className="user-info-header">
                      <strong>{b.first_name && b.last_name ? `${b.first_name} ${b.last_name}` : b.username}</strong>
                    </div>
                    {(b.email || b.phone || b.organization) && (
                      <button 
                        className="more-info-btn" 
                        onClick={() => toggleUserInfo(b.id)}
                      >
                        {expandedUserInfo.has(b.id) ? 'hide' : 'more info'}
                      </button>
                    )}
                    {expandedUserInfo.has(b.id) && (
                      <div className="user-info-expanded">
                        {b.organization && <small><strong>{b.organization}</strong></small>}
                        {b.email && <small>{b.email}</small>}
                        {b.phone && <small>{b.phone}</small>}
                      </div>
                    )}
                    {b.created_by && (
                      <small className="booked-by-admin">
                        📋 {b.created_by_first_name && b.created_by_last_name 
                          ? `${b.created_by_first_name} ${b.created_by_last_name}` 
                          : b.created_by_username}
                      </small>
                    )}
                  </div>
                </td>
                <td className="time-slot-cell">
                  <div className="date-line">{formatDate(b.booking_date)} ({b.slot_count || 1} slot{(b.slot_count || 1) > 1 ? 's' : ''})</div>
                  <div className="time-line">{formatTime12h(b.time_slot)}</div>
                  <div className="status-line">
                    {getStatusBadge(b.status)}
                    {b.reminder_sent && (
                      <span 
                        className="reminder-sent-badge" 
                        title={`Reminder sent ${b.reminder_sent_at ? new Date(b.reminder_sent_at).toLocaleString() : ''}`}
                      >
                        📧
                      </span>
                    )}
                    {b.admin_notes && (
                      <span className="admin-notes-preview" title={b.admin_notes}>📝</span>
                    )}
                  </div>
                </td>
                <td>{b.booking_type === 'full_turf' ? 'Full Turf' : b.booking_type === 'half_turf' ? 'Half Turf' : b.booking_type === 'cage_rental' ? 'Cage' : b.booking_type}</td>
                <td>{b.price !== null && b.price !== undefined ? `$${Number(b.price).toFixed(2)}` : '-'}</td>
                <td className="requested-cell">
                  <div className="requested-date">{formatDate(b.created_at)}</div>
                  <div className="requested-time">{new Date(b.created_at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}</div>
                </td>
                <td>
                  <div className="actions-cell">
                  {b.status === 'pending' && (
                    <>
                      <button 
                        className="approve-btn" 
                        onClick={() => openNotesModal(b.id, 'approved')}
                        disabled={!canApproveBookingType(b)}
                        title={!canApproveBookingType(b) ? 'Cannot approve: slot is fully booked or incompatible' : 'Approve this booking'}
                      >
                        Approve
                      </button>
                      <button className="reject-btn" onClick={() => openNotesModal(b.id, 'rejected')}>
                        Reject
                      </button>
                    </>
                  )}
                  {b.status === 'rejected' && (
                    <button className="approve-btn" onClick={() => openNotesModal(b.id, 'approved')}>
                      Approve
                    </button>
                  )}
                  {b.status === 'approved' && (
                    <button 
                      className="remind-btn" 
                      onClick={() => handleSendReminder(b.id)}
                      title={b.reminder_sent ? `Reminder already sent ${b.reminder_sent_at ? new Date(b.reminder_sent_at).toLocaleString() : ''}` : 'Send reminder email to customer'}
                    >
                      Remind
                    </button>
                  )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Notes Modal for Single Action */}
      {notesModal.open && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>{notesModal.action === 'approved' ? 'Approve' : 'Reject'} Booking</h3>
            <p>
              {notesModal.action === 'rejected' 
                ? <><strong>Reason required:</strong> Please explain why this booking is being rejected.</>
                : 'Add optional notes for this action:'}
            </p>
            <textarea
              value={notesModal.notes}
              onChange={e => setNotesModal({ ...notesModal, notes: e.target.value })}
              placeholder={notesModal.action === 'rejected' ? 'Enter reason for rejection (required)...' : 'Enter notes (optional)...'}
              rows={3}
              required={notesModal.action === 'rejected'}
            />
            {notesModal.action === 'rejected' && (
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '12px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={notesModal.sendNotification}
                  onChange={e => setNotesModal({ ...notesModal, sendNotification: e.target.checked })}
                  style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                />
                <span>📧 Send email notification to customer with reason</span>
              </label>
            )}
            <div className="modal-actions">
              <button className="modal-cancel" onClick={() => setNotesModal({ open: false, id: null, action: null, notes: '', sendNotification: true })}>
                Cancel
              </button>
              <button
                className={notesModal.action === 'approved' ? 'modal-approve' : 'modal-reject'}
                onClick={confirmNotesModal}
                disabled={notesModal.action === 'rejected' && !notesModal.notes.trim()}
              >
                {notesModal.action === 'approved' ? 'Approve' : 'Reject'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Notes Modal for Bulk Action */}
      {bulkNotesModal.open && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>{bulkNotesModal.action === 'approved' ? 'Approve' : 'Reject'} {selectedIds.length} Booking(s)</h3>
            <p>
              {bulkNotesModal.action === 'rejected'
                ? <><strong>Reason required:</strong> Please explain why these bookings are being rejected.</>
                : 'Add optional notes for this bulk action:'}
            </p>
            <textarea
              value={bulkNotesModal.notes}
              onChange={e => setBulkNotesModal({ ...bulkNotesModal, notes: e.target.value })}
              placeholder={bulkNotesModal.action === 'rejected' ? 'Enter reason for rejection (required)...' : 'Enter notes (optional)...'}
              rows={3}
            />
            {bulkNotesModal.action === 'rejected' && (
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '12px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={bulkNotesModal.sendNotification}
                  onChange={e => setBulkNotesModal({ ...bulkNotesModal, sendNotification: e.target.checked })}
                  style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                />
                <span>📧 Send email notification to customers with reason</span>
              </label>
            )}
            <div className="modal-actions">
              <button className="modal-cancel" onClick={() => setBulkNotesModal({ open: false, action: null, notes: '', sendNotification: true })}>
                Cancel
              </button>
              <button
                className={bulkNotesModal.action === 'approved' ? 'modal-approve' : 'modal-reject'}
                onClick={confirmBulkNotesModal}
                disabled={bulkNotesModal.action === 'rejected' && !bulkNotesModal.notes.trim()}
              >
                {bulkNotesModal.action === 'approved' ? 'Approve' : 'Reject'} All
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {deleteModal.open && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>Delete Booking</h3>
            <p><strong>Reason required:</strong> Please explain why this booking is being deleted.</p>
            <textarea
              value={deleteModal.reason}
              onChange={e => setDeleteModal({ ...deleteModal, reason: e.target.value })}
              placeholder="Enter reason for deletion (required)..."
              rows={3}
            />
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '12px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={deleteModal.sendNotification}
                onChange={e => setDeleteModal({ ...deleteModal, sendNotification: e.target.checked })}
                style={{ width: '16px', height: '16px', cursor: 'pointer' }}
              />
              <span>📧 Send email notification to customer with reason</span>
            </label>
            <div className="modal-actions">
              <button className="modal-cancel" onClick={() => setDeleteModal({ open: false, id: null, reason: '', sendNotification: true })}>
                Cancel
              </button>
              <button
                className="modal-reject"
                onClick={confirmDeleteModal}
                disabled={!deleteModal.reason.trim()}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminBookings;
